import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useFiatMarket } from '../context/FiatMarketContext';
import {
    LuSearch,
    LuRefreshCw,
    LuBanknote,
    LuStar,
    LuStarOff,
    LuArrowUpDown,
    LuClock,
    LuGlobe,
    LuWifiOff,
    LuZap,
    LuLandmark
} from 'react-icons/lu';

function FiatMarket() {
    const { lang } = useLanguage();
    const { forex, loading, connected, lastUpdate, refresh } = useFiatMarket();

    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('fiatMarketFavorites');
        return saved ? JSON.parse(saved) : ['USD/TRY', 'EUR/TRY', 'GBP/TRY'];
    });
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Save favorites
    useEffect(() => {
        localStorage.setItem('fiatMarketFavorites', JSON.stringify(favorites));
    }, [favorites]);

    const toggleFavorite = (code) => {
        setFavorites(prev =>
            prev.includes(code)
                ? prev.filter(s => s !== code)
                : [...prev, code]
        );
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await refresh();
        setTimeout(() => setIsRefreshing(false), 500);
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortData = (data) => {
        if (!data || !sortConfig.key) return data;
        return [...data].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const filterData = (data) => {
        if (!data) return [];
        if (!searchTerm) return data;
        const term = searchTerm.toLowerCase();
        return data.filter(item =>
            item.code?.toLowerCase().includes(term) ||
            item.name?.toLowerCase().includes(term) ||
            item.symbol?.toLowerCase().includes(term)
        );
    };

    const formatPrice = (price, decimals = 4) => {
        if (!price) return '-';
        return price.toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };

    return (
        <div className="fiat-market-page">
            <div className="page-header home-header">
                <div className="page-header-content">
                    <div className="page-title-group">
                        <div>
                            <h1 className="page-title">
                                <LuBanknote className="page-icon" />
                                {lang === 'tr' ? 'Döviz Kurları' : 'Exchange Rates'}
                            </h1>
                            <p className="page-subtitle">
                                <LuLandmark />
                                {lang === 'tr' ? 'T.C. Merkez Bankası Resmi Kurları' : 'Central Bank of Turkey Official Rates'}
                            </p>
                        </div>
                    </div>
                    <div className="page-actions">
                        {connected ? (
                            <span className="live-indicator tcmb">
                                <LuZap className="live-icon" />
                                <span>TCMB</span>
                            </span>
                        ) : (
                            <span className="status-badge disconnected">
                                <LuWifiOff /> {lang === 'tr' ? 'Bağlantı yok' : 'Disconnected'}
                            </span>
                        )}
                        {lastUpdate && (
                            <span className="last-update">
                                <LuClock />
                                {lastUpdate.toLocaleTimeString('tr-TR')}
                            </span>
                        )}
                        <button
                            className={`refresh-btn ${isRefreshing ? 'loading' : ''}`}
                            onClick={handleRefresh}
                            disabled={isRefreshing || loading}
                        >
                            <LuRefreshCw />
                        </button>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="market-controls">
                <div className="search-box">
                    <LuSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder={lang === 'tr' ? 'Döviz ara...' : 'Search currency...'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Loading State */}
            {loading && forex.length === 0 && (
                <div className="market-loading">
                    <div className="spinner"></div>
                    <p>{lang === 'tr' ? 'TCMB verileri yükleniyor...' : 'Loading TCMB data...'}</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && forex.length === 0 && (
                <div className="no-data-message">
                    <LuBanknote className="no-data-icon" />
                    <h3>{lang === 'tr' ? 'Döviz Verileri Yüklenemedi' : 'Currency Data Unavailable'}</h3>
                    <p>{lang === 'tr' ? 'TCMB verilerine ulaşılamıyor. Lütfen daha sonra tekrar deneyin.' : 'Unable to reach TCMB data. Please try again later.'}</p>
                </div>
            )}

            {/* Forex Table */}
            {forex.length > 0 && (
                <div className="market-table-container">
                    <table className="market-table forex-table tcmb-table">
                        <thead>
                            <tr>
                                <th className="fav-col"></th>
                                <th onClick={() => handleSort('code')} className="sortable">
                                    {lang === 'tr' ? 'Parite' : 'Pair'}
                                    <LuArrowUpDown />
                                </th>
                                <th onClick={() => handleSort('name')} className="sortable">
                                    {lang === 'tr' ? 'Döviz' : 'Currency'}
                                    <LuArrowUpDown />
                                </th>
                                <th onClick={() => handleSort('forexBuy')} className="sortable text-right">
                                    {lang === 'tr' ? 'Döviz Alış' : 'Forex Buy'}
                                    <LuArrowUpDown />
                                </th>
                                <th onClick={() => handleSort('forexSell')} className="sortable text-right">
                                    {lang === 'tr' ? 'Döviz Satış' : 'Forex Sell'}
                                    <LuArrowUpDown />
                                </th>
                                <th onClick={() => handleSort('banknoteBuy')} className="sortable text-right hide-mobile">
                                    {lang === 'tr' ? 'Efektif Alış' : 'Banknote Buy'}
                                    <LuArrowUpDown />
                                </th>
                                <th onClick={() => handleSort('banknoteSell')} className="sortable text-right hide-mobile">
                                    {lang === 'tr' ? 'Efektif Satış' : 'Banknote Sell'}
                                    <LuArrowUpDown />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortData(filterData(forex)).map(rate => (
                                <tr key={rate.symbol}>
                                    <td className="fav-col">
                                        <button
                                            className="fav-btn"
                                            onClick={() => toggleFavorite(rate.code)}
                                        >
                                            {favorites.includes(rate.code)
                                                ? <LuStar className="fav-icon active" />
                                                : <LuStarOff className="fav-icon" />
                                            }
                                        </button>
                                    </td>
                                    <td className="symbol-cell">
                                        <LuGlobe className="forex-icon" />
                                        <span className="forex-symbol">{rate.code}</span>
                                    </td>
                                    <td className="name-cell">{rate.name}</td>
                                    <td className="text-right price-cell">₺{formatPrice(rate.forexBuy)}</td>
                                    <td className="text-right price-cell">₺{formatPrice(rate.forexSell)}</td>
                                    <td className="text-right price-cell hide-mobile">₺{formatPrice(rate.banknoteBuy)}</td>
                                    <td className="text-right price-cell hide-mobile">₺{formatPrice(rate.banknoteSell)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Summary Cards */}
            {forex.length > 0 && (
                <div className="market-summary">
                    {forex.slice(0, 4).map(rate => (
                        <div key={rate.symbol} className="summary-card">
                            <h4>{rate.code}</h4>
                            <span className="summary-value">₺{formatPrice(rate.forexSell)}</span>
                            <span className="summary-label">{rate.name}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Data Source Note */}
            <div className="data-source-note">
                <p>
                    <LuLandmark />
                    {lang === 'tr'
                        ? 'Veriler T.C. Merkez Bankası resmi kurlarından alınmaktadır. Kurlar her iş günü saat 15:30\'da güncellenir.'
                        : 'Data from Central Bank of Turkey official rates. Rates are updated at 15:30 on business days.'}
                </p>
            </div>
        </div>
    );
}

export default FiatMarket;
